import { createContext, useContext } from 'react'

import { ViewColumnType, ViewCustomColumnTemplateBase, ViewType } from 'types/graphql'

import { ColumnHeaderActionsConfig } from 'src/govwell-ui/components/Table/headerActions'
import { ColumnManager } from 'src/models/TableViews/ColumnManager'
import { FilterManager } from 'src/models/TableViews/FilterManager'
import { TableViewColumnTemplateStore } from 'src/models/TableViews/TableViewColumnTemplateStore'

/**
 * Shared seam for column/filter management. Two providers populate it:
 * `SavedTableViewConfigProvider` (persists via `updateView`) and
 * `ReportConfigProvider` (`onSave` is a no-op — report persistence
 * runs from the drawer's Save button). Nullable fields encode "no
 * view-type selected yet"; consumers must guard.
 */
export type TableViewConfigContextValue = {
  filterManager: FilterManager | null
  columnManager: ColumnManager | null
  columnTemplateStore: TableViewColumnTemplateStore
  customizableColumnTemplates: ViewCustomColumnTemplateBase[]
  customizableColumnTemplatesByType: Map<ViewColumnType, ViewCustomColumnTemplateBase>
  hasCustomizableColumnTemplates: boolean
  viewType: ViewType | null
  recordTypeId: number | null
  /** Org-wide views (e.g. Tasks) scope candidate discovery via org, not record type. */
  organizationId: number | null
  viewDashboardId: number | null
  /** Saved-table-view: persists via `updateView`. Report: no-op (deferred to Save button). */
  onSave: () => Promise<unknown>
  /**
   * Per-column header popover actions (sort / rename / hide). Populated
   * by the report-preview surface (`ReportConfigProvider`); set to
   * `null` by the saved table view surface — saved-view header
   * affordances ship in a later milestone. `TableHeaderCell` renders
   * the chevron only when this is non-null.
   */
  headerActions: ColumnHeaderActionsConfig | null
}

export const TableViewConfigContext = createContext<TableViewConfigContextValue | null>(null)

/** Throws outside a provider so a missing wrap surfaces as a render error. */
export const useTableViewConfig = (): TableViewConfigContextValue => {
  const ctx = useContext(TableViewConfigContext)
  if (!ctx) {
    throw new Error(
      'useTableViewConfig must be used within a TableViewConfigProvider (SavedTableViewConfigProvider or ReportConfigProvider)'
    )
  }
  return ctx
}
